import { getCSSVariableValue } from '@backoffice-frontend/common'
import { useErrorHandlingLazyQuery } from '@backoffice-frontend/data-fetching'
import { GuidepostIcon } from '@moia-dev/pace-icons'
import type { LayersControlEvent } from 'leaflet'
import { divIcon } from 'leaflet'
import { useEffect, useMemo, useState } from 'react'
import ReactDOMServer from 'react-dom/server'
import { FeatureGroup, Marker, useMap } from 'react-leaflet'
import { useServiceAreaAnchorPointsLazyQuery } from '../../graphql/query/serviceAreaAnchorPoints.hook'

const ICON_SIZE = 24
const BADGE_SIZE = 32

// Built at render time — reading the CSS variables at module load returns an
// empty string because the theme is not yet applied to the document body.
const createAnchorIcon = () =>
  divIcon({
    html: ReactDOMServer.renderToStaticMarkup(
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: BADGE_SIZE,
          width: BADGE_SIZE,
          borderRadius: '50%',
          backgroundColor: 'white',
          boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.3)',
          color: getCSSVariableValue('--color-content-default'),
        }}
      >
        <GuidepostIcon style={{ height: ICON_SIZE, width: ICON_SIZE }} />
      </div>,
    ),
    className: 'routing-anchor-icon',
    iconSize: [BADGE_SIZE, BADGE_SIZE],
    iconAnchor: [BADGE_SIZE / 2, BADGE_SIZE / 2],
  })

type Props = {
  serviceAreaId: string
  overlayName: string
  initiallyActive?: boolean
}

/**
 * Renders the service area's routing anchor points as guidepost markers.
 * Nested inside a `LayersControl.Overlay` so it can be toggled. The anchor
 * points are fetched lazily — not until the overlay is first enabled — so the
 * shared MoiaMap does not fetch them for consumers that never open the layer.
 * The query is cache-first, so re-enabling the overlay serves cached results
 * rather than hitting the network again.
 */
export const RoutingAnchorsLayer = ({
  serviceAreaId,
  overlayName,
  initiallyActive = false,
}: Props) => {
  const map = useMap()
  const anchorIcon = useMemo(() => createAnchorIcon(), [])
  const [isOverlayActive, setIsOverlayActive] = useState(initiallyActive)
  const [fetchAnchorPoints, { data }] = useErrorHandlingLazyQuery(
    useServiceAreaAnchorPointsLazyQuery,
  )

  useEffect(() => {
    const handleOverlayAdd = (event: LayersControlEvent) => {
      if (event.name === overlayName) setIsOverlayActive(true)
    }
    const handleOverlayRemove = (event: LayersControlEvent) => {
      if (event.name === overlayName) setIsOverlayActive(false)
    }

    map.on('overlayadd', handleOverlayAdd)
    map.on('overlayremove', handleOverlayRemove)

    return () => {
      map.off('overlayadd', handleOverlayAdd)
      map.off('overlayremove', handleOverlayRemove)
    }
  }, [map, overlayName])

  useEffect(() => {
    if (isOverlayActive) {
      void fetchAnchorPoints({ variables: { serviceAreaId } })
    }
  }, [fetchAnchorPoints, isOverlayActive, serviceAreaId])

  const anchorPoints = isOverlayActive
    ? (data?.serviceAreaAnchorPoints ?? [])
    : []

  return (
    <FeatureGroup>
      {anchorPoints.map(anchorPoint => (
        <Marker
          key={`${anchorPoint.name}-${anchorPoint.location.latitude}-${anchorPoint.location.longitude}`}
          position={[
            anchorPoint.location.latitude,
            anchorPoint.location.longitude,
          ]}
          icon={anchorIcon}
        />
      ))}
    </FeatureGroup>
  )
}
